import { Link, Skeleton } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { useGebruiker } from '../api/gebruikers.ts';
import { useKandidaat } from '../api/kandidaten.ts';
import { useBedrijf, useContactpersonenOverzicht } from '../api/klanten.ts';
import { useVacature } from '../api/vacatures.ts';
import { volledigeNaam } from '../helpers/formatteer.ts';

function Verwijzing({ naar, tekst, laden }: { naar: string; tekst: string | undefined; laden: boolean }) {
  if (tekst === undefined) {
    return laden ? <Skeleton width={120} sx={{ display: 'inline-block' }} /> : <>Onbekend</>;
  }
  return (
    <Link component={RouterLink} to={naar} underline="hover" sx={{ fontWeight: 500 }}>
      {tekst}
    </Link>
  );
}

/**
 * De naam van een kandidaat als link naar zijn profiel, opgehaald op id.
 * @param props Het id van de kandidaat.
 * @returns De link.
 */
export function KandidaatVerwijzing({ id }: { id: string }) {
  const kandidaat = useKandidaat(id);
  return <Verwijzing naar={`/kandidaten/${id}`} tekst={kandidaat.data?.volledigeNaam} laden={kandidaat.isPending} />;
}

/**
 * De titel van een vacature als link, opgehaald op id.
 * @param props Het id van de vacature.
 * @returns De link.
 */
export function VacatureVerwijzing({ id }: { id: string }) {
  const vacature = useVacature(id);
  return <Verwijzing naar={`/vacatures/${id}`} tekst={vacature.data?.titel} laden={vacature.isPending} />;
}

/**
 * De naam van een bedrijf als link, opgehaald op id.
 * @param props Het id van het bedrijf.
 * @returns De link.
 */
export function BedrijfVerwijzing({ id }: { id: string }) {
  const bedrijf = useBedrijf(id);
  return <Verwijzing naar={`/bedrijven/${id}`} tekst={bedrijf.data?.naam} laden={bedrijf.isPending} />;
}

/**
 * De naam van een contactpersoon als link naar zijn bedrijf.
 * @param props Het id van de contactpersoon.
 * @returns De link.
 */
export function ContactpersoonVerwijzing({ id }: { id: string }) {
  const overzicht = useContactpersonenOverzicht();
  const contactpersoon = overzicht.data?.get(id);
  if (!contactpersoon) {
    return overzicht.isPending ? <Skeleton width={120} sx={{ display: 'inline-block' }} /> : <>Contactpersoon</>;
  }
  return (
    <Link
      component={RouterLink}
      to={`/bedrijven/${contactpersoon.bedrijfId}`}
      underline="hover"
      sx={{ fontWeight: 500 }}
    >
      {volledigeNaam(contactpersoon)}
    </Link>
  );
}

/**
 * De naam van een gebruiker, opgehaald op id.
 * @param props Het id van de gebruiker.
 * @returns De naam.
 */
export function GebruikerNaam({ id }: { id: string | null }) {
  const gebruiker = useGebruiker(id);
  if (!id) {
    return <>het systeem</>;
  }
  return <>{gebruiker.data?.naam ?? (gebruiker.isPending ? '…' : 'onbekende gebruiker')}</>;
}
